import type { RollupWithTagsAndCountsAndRange } from "types/types";
import { PnlCategoriesEnum } from "types/enums";
import styled from "styled-components/macro";
import {
  PNL_COLORS,
  PNL_ORDER,
  ALL_PNL_CATEGORIES,
  BREAKPOINT_TABLET,
} from "constants/constants";
import { getPnlFormattedHoursMinutes } from "utils/timeUtils";
import { useGetPageFromPath } from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { camelCase } from "lodash";
import { useSettings } from "contexts/SettingsManager/SettingsManager";

const OverallBarstackTooltipContent = ({
  rollupDisplay,
  filterCategoriesInPercent = [],
  style = {},
}: {
  rollupDisplay: RollupWithTagsAndCountsAndRange;
  filterCategoriesInPercent?: PnlCategoriesEnum[];
  style?: React.CSSProperties;
}) => {
  const { isTrendsPage } = useGetPageFromPath();
  const { languagePreference } = useSettings();
  const isFrench = languagePreference?.slice(0, 2) === "fr";

  return (
    <TooltipContentStyles
      {...{ $isTrendsPage: isTrendsPage, $isFrench: isFrench, ...style }}
    >
      {Object.entries(rollupDisplay)
        .filter(([category, val]) =>
          Boolean(
            PnlCategoriesEnum[category] &&
              !filterCategoriesInPercent.includes(PnlCategoriesEnum[category])
          )
        )
        .sort((a, b) => PNL_ORDER[a[0]] - PNL_ORDER[b[0]])
        .map(([category, val]) => {
          const totalDurationMs =
            rollupDisplay.totalDurationMs -
            filterCategoriesInPercent
              .map(category => rollupDisplay[category])
              .reduce((acc, val) => acc + val, 0);
          const percent =
            (val || val === 0) && totalDurationMs !== 0
              ? Number(val) / totalDurationMs
              : null;
          return (
            <TooltipRow
              key={category}
              category={category}
              percent={percent}
              time={val}
            />
          );
        })}
    </TooltipContentStyles>
  );
};

export default OverallBarstackTooltipContent;

const TooltipRow = ({ category, percent, time }) => {
  const categoryInfo = ALL_PNL_CATEGORIES.find(c => c.category === category);
  const { translate } = useFlightTranslation();
  const categoryTitle = categoryInfo
    ? translate(camelCase(categoryInfo.label))
    : null;

  const { hours, minutes } = getPnlFormattedHoursMinutes(time);
  return (
    <div className={`tooltipRow tooltipRow-${category}`}>
      <div className={`category${percent === 0 ? " zero" : ""}`}>
        <div
          className="coloredSquare"
          style={{ background: PNL_COLORS[category] }}
        />
        <div className="categoryTitle">{categoryTitle}</div>
      </div>
      <div className={`percent${percent === 0 ? " zero" : ""}`}>
        {(percent * 100).toFixed(0)}%
      </div>
      <div className="time">
        <div className={`hours${hours === "0" ? " zero" : ""}`}>{hours}h</div>
        <div className={`minutes${minutes === "0" ? " zero" : ""}`}>
          {Number(minutes) < 10 ? "0" : ""}
          {minutes}m
        </div>
      </div>
    </div>
  );
};

const ROW_WIDTH = 212;
const PADDING_OUTER = 24;

const rowWidth = (pad: boolean) => ROW_WIDTH + (pad ? 20 : 0);
const TooltipContentStyles = styled.div<{
  $isTrendsPage: boolean;
  $isFrench: boolean;
}>`
  pointer-events: auto;
  padding: ${PADDING_OUTER}px;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  line-height: 0;
  box-sizing: border-box;
  width: ${props => rowWidth(props.$isTrendsPage) + 2 * PADDING_OUTER}px;
  height: fit-content;
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    flex-wrap: wrap;
    align-content: space-between;
    height: 164px;
    width: ${props =>
      2 * rowWidth(props.$isTrendsPage) + 6.5 * PADDING_OUTER}px;
    margin-left: -${2 * PADDING_OUTER}px;
    margin-bottom: -16px;
    .tooltipRow {
      margin-left: ${2 * PADDING_OUTER}px;
    }
  }
  .tooltipRow {
    width: ${props => rowWidth(props.$isTrendsPage)}px;
    display: flex;
    padding-bottom: 16px;
    justify-content: space-between;
    box-sizing: border-box;
    &:last-child {
      padding-bottom: 0;
    }
  }
  .category {
    display: flex;
    align-items: center;
    width: ${props => (props.$isFrench ? 130 : 102)}px;
    font-weight: bold;
    width: 108px;
  }
  .categoryTitle {
    line-height: 1em;
  }
  .category,
  .percent,
  .time {
    flex-shrink: 0;
    box-sizing: border-box;
  }
  .percent,
  .time {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  .zero {
    opacity: 0.3;
  }
  .percent {
    width: 40px;
    padding-left: 12px;
  }
  .time {
    width: ${props => (props.$isTrendsPage ? 84 : 64)}px;
    padding-left: 24px;
    display: flex;
    .minutes {
      padding-left: 4px;
    }
  }
  .coloredSquare {
    box-sizing: border-box;
    width: 13px;
    height: 13px;
    border: 1px solid black;
    border-radius: 2px;
    margin-right: 6px;
  }
`;
